import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { useContext, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Label from '../../../components/form/Label'
import catchError from '../../../components/global/CatchError'
import { apiHeader, SERVER_URL } from '../../../config'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { ParentSkill } from '../../../types/skill.type'
import { StyledButton } from '../../admin/Login'
import { AdminForm } from '../../admin/users/AdminUsersForm'

type CoachParentSkillFormModalProps = {
    setShowEditSkillModal: (show: boolean) => void
    parentSkillToEdit?: ParentSkill
    setParentSkillToEdit: (skill?: ParentSkill) => void
    refetchParentSkills: () => void
}

const CoachParentSkillFormModal = ({
    setShowEditSkillModal,
    parentSkillToEdit,
    setParentSkillToEdit,
    refetchParentSkills,
}: CoachParentSkillFormModalProps) => {
    const user = useContext(UserContext)
    const [name, setName] = useState<string>(parentSkillToEdit?.name ?? '')
    const navigate = useNavigate()
    const lng = useLangSelected()

    const { mutate: editSkill } = useMutation({
        mutationFn: async () => {
            if (!name) {
                toast.error('Le nom est requis')
                return
            }

            await axios.put(
                `${SERVER_URL}/parent-skills/${parentSkillToEdit?.id}`,
                { name },
                apiHeader(user.tokenCoach)
            )
            toast.success('Catégorie mise à jour avec succès')
            refetchParentSkills()
            setShowEditSkillModal(false)
        },
        onError: (error: any) => {
            handleApiError(error, 'Erreur lors de la mise à jour de la catégorie')
        },
    })

    const { mutate: createSkill } = useMutation({
        mutationFn: async () => {
            if (!name) {
                toast.error('Le nom est requis')
                return
            }

            await axios.post(`${SERVER_URL}/parent-skills`, { name }, apiHeader(user.tokenCoach))
            toast.success('Catégorie créée avec succès')
            refetchParentSkills()
            setShowEditSkillModal(false)
        },
        onError: (error: any) => {
            handleApiError(error, 'Erreur lors de la création de la catégorie')
        },
    })

    const handleApiError = (error: any, defaultMessage: string) => {
        if (error.response?.status === 401) {
            catchError({ status: 401, message: 'Votre session a expiré' }, () => {
                localStorage.removeItem('tokenCoach')
                navigate(`/${lng}/login`)
            })
        } else {
            console.error(defaultMessage, error)
            toast.error(error.response?.data?.message ?? defaultMessage)
        }
    }

    const initForm = () => {
        setParentSkillToEdit(undefined)
        setShowEditSkillModal(false)
        setName('')
    }

    return (
        <Modal
            centered
            show={true}
            onHide={() => {
                initForm()
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title>{parentSkillToEdit ? 'Modifier' : 'Créer'} une catégorie de compétence</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <AdminForm
                    onSubmit={(e) => {
                        e.preventDefault()
                        if (parentSkillToEdit) editSkill()
                        else createSkill()
                    }}
                >
                    <Form.Group className="mb-3">
                        <Label>Nom *</Label>
                        <Form.Control
                            type="text"
                            placeholder="Nom de la catégorie *"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Form.Group>
                    <div className="d-flex justify-content-end gap-2">
                        <StyledButton variant="primary" type="submit" disabled={!name.trim()}>
                            {parentSkillToEdit ? 'Mettre à jour' : 'Créer'}
                        </StyledButton>
                    </div>
                </AdminForm>
            </Modal.Body>
        </Modal>
    )
}

export default CoachParentSkillFormModal
